import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { QueryRoute, RouteStore } from 'bernie-core';

import { SearchResultsStore } from 'stores/search-results-store';

import { SearchFormFields } from 'models';

import { FiltersDialog } from './filters-dialog';

export interface FiltersToggleProps {
  className?: string;
  routeStore?: RouteStore;
  searchResults?: SearchResultsStore;
  showActions?: boolean;
  isActive: boolean;
  buttonComponent: React.ReactNode;
  isFullWidth: boolean;
  label: string;
  a11yMessage: string;
  rfrr: string;
  stid: string;
  filters: SearchFormFields;
  dialogParam: string;
  dialogTitle: string;
}
export type LocalizedFiltersToggleProps = LocalizedComponentProps & FiltersToggleProps;

@withLocalization
@inject('routeStore', 'searchResults')
@observer
export class FiltersToggle extends React.Component<LocalizedFiltersToggleProps> {
  private filtersBtn: React.RefObject<HTMLElement>;

  constructor(props: LocalizedFiltersToggleProps) {
    super(props);

    this.filtersBtn = React.createRef();
    this.onDismiss = this.onDismiss.bind(this);
    this.state = { mounted: false };
  }

  componentDidMount() {
    // This is a hack to fix https://jira.expedia.biz/browse/BHS-20095
    // It is likely a bug in bernie that was unnoticed because the re-render fixed by #4171 was hiding this bug
    setTimeout(() => this.setState({ mounted: true }), 0);
  }

  public render() {
    const {
      isActive = false,
      searchResults,
      routeStore,
      buttonComponent,
      isFullWidth,
      label,
      a11yMessage,
      rfrr,
      stid,
      filters,
      dialogParam,
      dialogTitle,
      l10n,
    } = this.props;

    return (
      <React.Fragment>
        <FiltersButton
          a11yMessage={a11yMessage}
          buttonComponent={buttonComponent}
          dialogParam={dialogParam}
          filtersBtn={this.filtersBtn}
          isActive={isActive}
          isFullWidth={isFullWidth}
          l10n={l10n}
          label={label}
          rfrr={rfrr}
          routeStore={routeStore}
          searchResults={searchResults}
          stid={stid}
        />
        <QueryRoute query={{ pwaDialog: dialogParam }}>
          <Layer id="tools-filterdrawer" onDismiss={this.onDismiss}>
            <FiltersDialog
              searchCriteria={searchResults.searchCriteria}
              onDismiss={this.onDismiss}
              {...{ searchResults, filters, dialogTitle }}
            />
          </Layer>
        </QueryRoute>
      </React.Fragment>
    );
  }

  private onDismiss(): void {
    const goBack = this.props?.routeStore?.goBack;
    const focus = this.filtersBtn?.current?.focus;

    if (typeof goBack === 'function') {
      this.props.routeStore.goBack();
    }

    if (typeof focus === 'function') {
      this.filtersBtn.current.focus();
    }
  }
}

const FiltersButton = ({
  a11yMessage,
  buttonComponent,
  dialogParam,
  filtersBtn,
  isActive,
  isFullWidth,
  l10n,
  label,
  rfrr,
  routeStore,
  searchResults,
  stid,
}) => {
  const { isLoading, isError, sortAndFilter } = searchResults;
  const appliedFilterCount = sortAndFilter?.options?.appliedFilterCount;
  const ButtonTracked = ClickTracker(buttonComponent);
  const filtersApplied = appliedFilterCount > 0;
  const ariaLabel = `${filtersApplied ? `${l10n.formatText(a11yMessage, appliedFilterCount)}, ` : ''}${l10n.formatText(label)}`;
  const hasFilter = sortAndFilter && Object.keys(sortAndFilter).length > 0;
  const openFiltersQuery = routeStore.mergeQueryString(routeStore.search, { pwaDialog: dialogParam });
  const extraProps =
    isActive && hasFilter
      ? {
          tag: Link,
          role: 'button',
          to: {
            pathname: routeStore.pathname,
            search: openFiltersQuery,
          },
        }
      : {};

  return (
    <ButtonTracked
      innerRef={filtersBtn}
      data-stid={stid}
      disabled={!isActive || isLoading || isError || null}
      linkName="Launch filters takeover"
      rfrr={rfrr}
      icon={filtersApplied ? null : 'tune'}
      badgeText={filtersApplied ? appliedFilterCount : null}
      badgeType="notification-1"
      text={l10n.formatText(label)}
      isFullWidth={isFullWidth}
      aria-label={ariaLabel}
      {...extraProps}
    />
  );
};
